import React, { useContext, useState } from 'react'
import { useMutation } from 'react-apollo'

import { SelectionList } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { compose } from 'recompose'
import { v4 as uuidv4 } from 'uuid'

import CenteredSpinner from 'components/CenteredSpinner'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { ActionType, PageName } from 'generated/types'
import { useFundingInitiatedPolling } from 'pages/Funding/Checking/useFundingInitiatedPolling'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext } from 'utils/FunnelContext'
import { ModalActionTypes, PromptProps, withPrompt } from 'utils/prompts'

import { useCheckingTransferTracking } from './events'
import initiateTransferMutation from './gql/initiate-transfer-mutation.gql'
import { Content, Heading, NextButton, SubHeading } from './styled-components'
import { useTransferAccounts } from './useTransferAccounts'

type OneTimeDepositScreenProps = WithFlowRouterProps & PromptProps

const DEFAULT_DEPOSIT_AMOUNT = '100'
const depositOptions = ['250', '100', '25'].map(n => ({
  label: `$${n}`,
  value: n,
}))

const enhance = compose<OneTimeDepositScreenProps, {}>(withFlowRouter, withPrompt)

export const OneTimeDeposit = enhance(({ route, promptUser }) => {
  const track = useCheckingTransferTracking('one-time')
  const {
    checkingAccountId,
    externalFundingSourceId,
    loading: loadingTransferAccounts,
  } = useTransferAccounts()
  const { isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const { getRemoteString } = useRemoteStrings()
  const nonce = uuidv4()

  const [selectedAmount, setSelectedAmount] = useState(DEFAULT_DEPOSIT_AMOUNT)

  const { startPolling, isPolling } = useFundingInitiatedPolling(() => {
    isDynamicFunnelEnabled
      ? routeToPage(PageName.OneTimeDeposit, ActionType.Continue)
      : route({ from: FlowRoute.OneTimeDeposit, action: 'continue' })
  })

  const [initiateTransfer, { loading }] = useMutation<any>(initiateTransferMutation, {
    onCompleted: startPolling,
  })

  const onSetDeposit = () => {
    track.ctaTapped(selectedAmount)
    track.confirmModalViewed()
    promptUser({
      image: null,
      title: getRemoteString({
        key: 'checking.card_order.fund_account.one_time.alert.title',
      }),
      message: getRemoteString({
        key: 'checking.card_order.fund_account.one_time.alert.body.variable',
        variables: [`$${selectedAmount}`],
      }),
      actions: [
        {
          type: ModalActionTypes.secondary,
          label: getRemoteString({
            key: 'checking.card_order.fund_account.alert.cta.secondary',
          }),
        },
        {
          type: ModalActionTypes.primary,
          label: getRemoteString({
            key: 'checking.card_order.fund_account.alert.cta.primary',
          }),
        },
      ],
    }).then(result => {
      if (result.index === 1) {
        track.confirmModalCtaTapped()
        initiateTransfer({
          variables: {
            amount: parseFloat(selectedAmount),
            fromAccountId: externalFundingSourceId,
            toAccountId: checkingAccountId,
            nonce,
          },
        })
      } else if (result.index === 0) {
        track.confirmModalCancelTapped()
      }
    })
  }

  if (loading || loadingTransferAccounts || isPolling) {
    return <CenteredSpinner />
  }

  return (
    <>
      <Nav />
      <Layout>
        <Content>
          <Heading>
            {getRemoteString({
              key: 'checking.card_order.fund_account.one_time.header',
            })}
          </Heading>
          <SubHeading>
            {getRemoteString({
              key: 'checking.card_order.fund_account.one_time.body',
            })}
          </SubHeading>
          <SelectionList
            className={'selection-list'}
            value={selectedAmount}
            options={depositOptions}
            onSelect={option => {
              setSelectedAmount(option.value)
            }}
          />
        </Content>
        <Separator />
        <NextButton onPress={onSetDeposit}>
          {getRemoteString({
            key: 'checking.card_order.fund_account.one_time.cta.primary',
          })}
        </NextButton>
      </Layout>
    </>
  )
})
